import { CouponNewUserSurveyAnswer, CouponNewUserSurveyQuestion } from '@/api/types/couponType';
import { CouponNewUserSurveyQuestionAnswerState } from '@/components/store/couponNewUserSurvey/type/CouponNewUserSurveyType';
import {
  COUPON_NEW_USER_SURVEY_GTM_DEFAULT_ENTRY_POINT,
  COUPON_NEW_USER_SURVEY_GTM_ENTRY_POINT_BY_PATHNAME,
  COUPON_NEW_USER_SURVEY_GTM_HOME_PAGE_NAME,
  COUPON_NEW_USER_SURVEY_GTM_PROFILE_PROPERTY_BY_QUESTION_KEY,
  COUPON_NEW_USER_SURVEY_GTM_QUESTION_ID_BY_KEY,
} from '@/core/common/constant/coupon/CouponNewUserSurveyConstant';
import {
  CouponNewUserSurveyGtmDevice,
  CouponNewUserSurveyQuestionType,
  CouponNewUserSurveyWizardStep,
} from '@/core/common/enum/coupon/CouponNewUserSurveyEnum';
import {
  CouponNewUserSurveyWizardPosition,
  CouponNewUserSurveyWizardStepTransitions,
} from '@/helpers/coupon/type/CouponNewUserSurveyHelperType';

export const getOtherOption = (question: CouponNewUserSurveyQuestion) =>
  question.options.find((option) => option.allowOther);

export const isOtherOptionSelected = (
  question: CouponNewUserSurveyQuestion,
  answer: CouponNewUserSurveyQuestionAnswerState,
): boolean => {
  const otherOption = getOtherOption(question);
  return otherOption ? answer.selectedOptionIds.includes(otherOption.id) : false;
};

export const selectSingleOption = (
  question: CouponNewUserSurveyQuestion,
  answer: CouponNewUserSurveyQuestionAnswerState,
  optionId: string,
): CouponNewUserSurveyQuestionAnswerState => ({
  selectedOptionIds: [optionId],
  otherText: optionId === getOtherOption(question)?.id ? answer.otherText : '',
});

export const toggleMultipleOption = (
  question: CouponNewUserSurveyQuestion,
  answer: CouponNewUserSurveyQuestionAnswerState,
  optionId: string,
): CouponNewUserSurveyQuestionAnswerState => {
  const selectedOptionIds = answer.selectedOptionIds.includes(optionId)
    ? answer.selectedOptionIds.filter((id) => id !== optionId)
    : [...answer.selectedOptionIds, optionId];
  const otherOptionId = getOtherOption(question)?.id;

  return {
    selectedOptionIds,
    otherText: otherOptionId && selectedOptionIds.includes(otherOptionId) ? answer.otherText : '',
  };
};

const stayOnPosition = (position: CouponNewUserSurveyWizardPosition) => position;

const wizardStepTransitions: Record<CouponNewUserSurveyWizardStep, CouponNewUserSurveyWizardStepTransitions> = {
  [CouponNewUserSurveyWizardStep.Intro]: {
    next: () => ({ step: CouponNewUserSurveyWizardStep.Question, questionIndex: 0 }),
    back: stayOnPosition,
  },
  [CouponNewUserSurveyWizardStep.Question]: {
    next: (position, totalQuestions) =>
      position.questionIndex < totalQuestions - 1
        ? { ...position, questionIndex: position.questionIndex + 1 }
        : { ...position, step: CouponNewUserSurveyWizardStep.Email },
    back: (position) =>
      position.questionIndex > 0
        ? { ...position, questionIndex: position.questionIndex - 1 }
        : { step: CouponNewUserSurveyWizardStep.Intro, questionIndex: 0 },
  },
  [CouponNewUserSurveyWizardStep.Email]: {
    // Email moves forward by submitting the claim, not by a wizard transition.
    next: stayOnPosition,
    back: (_position, totalQuestions) => ({
      step: CouponNewUserSurveyWizardStep.Question,
      questionIndex: Math.max(totalQuestions - 1, 0),
    }),
  },
};

export const getNextWizardPosition = (
  position: CouponNewUserSurveyWizardPosition,
  totalQuestions: number,
): CouponNewUserSurveyWizardPosition => wizardStepTransitions[position.step].next(position, totalQuestions);

export const getPreviousWizardPosition = (
  position: CouponNewUserSurveyWizardPosition,
  totalQuestions: number,
): CouponNewUserSurveyWizardPosition => wizardStepTransitions[position.step].back(position, totalQuestions);

export const createEmptySurveyAnswers = (
  questions: CouponNewUserSurveyQuestion[],
): CouponNewUserSurveyQuestionAnswerState[] =>
  questions.map(() => ({
    selectedOptionIds: [],
    otherText: '',
  }));

// Enables "next" without the "other" text so the click can surface the other-required error.
export const isSurveyAnswerSelected = (
  question: CouponNewUserSurveyQuestion,
  answer: CouponNewUserSurveyQuestionAnswerState,
): boolean => {
  if (!answer || answer.selectedOptionIds.length === 0) {
    return false;
  }

  return question.type !== CouponNewUserSurveyQuestionType.Single || answer.selectedOptionIds.length === 1;
};

export const validateSurveyAnswer = (
  question: CouponNewUserSurveyQuestion,
  answer: CouponNewUserSurveyQuestionAnswerState,
): boolean => {
  if (!isSurveyAnswerSelected(question, answer)) {
    return false;
  }

  if (isOtherOptionSelected(question, answer) && !answer.otherText.trim()) {
    return false;
  }

  return true;
};

export const buildSurveyPayload = (
  questions: CouponNewUserSurveyQuestion[],
  answers: CouponNewUserSurveyQuestionAnswerState[],
): CouponNewUserSurveyAnswer[] =>
  questions.map((question, index) => {
    const answer = answers[index];
    const otherOption = getOtherOption(question);
    const hasOtherSelected = isOtherOptionSelected(question, answer);
    const otherText = hasOtherSelected ? answer.otherText.trim() : undefined;

    const selectedLabels = answer.selectedOptionIds
      .filter((optionId) => optionId !== otherOption?.id)
      .map((optionId) => question.options.find((option) => option.id === optionId)?.label)
      .filter((label): label is string => Boolean(label));

    let formattedAnswers = '';

    if (question.type === CouponNewUserSurveyQuestionType.Single) {
      formattedAnswers = selectedLabels[0] ?? (hasOtherSelected ? otherOption?.label ?? '' : '');
    } else if (selectedLabels.length > 0) {
      formattedAnswers = selectedLabels.join(', ');
    } else if (hasOtherSelected) {
      formattedAnswers = otherOption?.label ?? '';
    }

    const payload: CouponNewUserSurveyAnswer = {
      questions: question.label,
      answers: formattedAnswers,
    };

    if (otherText) {
      payload.other = otherText;
    }

    return payload;
  });

export const createSurveySessionId = (): string => {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  // RFC 4122 v4 fallback for browsers without crypto.randomUUID (e.g. older iPad Safari).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const random = Math.floor(Math.random() * 16);
    return (char === 'x' ? random : (random % 4) + 8).toString(16);
  });
};

export const getSurveyGtmQuestionId = (questionKey: string): string =>
  COUPON_NEW_USER_SURVEY_GTM_QUESTION_ID_BY_KEY[questionKey] ?? questionKey;

export const getSurveyGtmEntryPoint = (pathname: string): string =>
  COUPON_NEW_USER_SURVEY_GTM_ENTRY_POINT_BY_PATHNAME[pathname] ?? COUPON_NEW_USER_SURVEY_GTM_DEFAULT_ENTRY_POINT;

export const getSurveyGtmPageName = (pathname: string): string =>
  pathname === '/' ? COUPON_NEW_USER_SURVEY_GTM_HOME_PAGE_NAME : pathname;

export const getSurveyGtmDevice = (): CouponNewUserSurveyGtmDevice => {
  if (typeof navigator === 'undefined') {
    return CouponNewUserSurveyGtmDevice.Desktop;
  }

  const { userAgent, maxTouchPoints } = navigator;
  // iPadOS 13+ reports a Mac user agent, so fall back to touch support.
  if (/iPad/.test(userAgent) || (/Macintosh/.test(userAgent) && maxTouchPoints > 1)) {
    return CouponNewUserSurveyGtmDevice.IPad;
  }

  return /Mobi|Android|iPhone/.test(userAgent) ? CouponNewUserSurveyGtmDevice.Mobile : CouponNewUserSurveyGtmDevice.Desktop;
};

// Option ids only — "other" free text is never sent to GTM (PDPA).
export const buildSurveyGtmAnswersSoFar = (
  questions: CouponNewUserSurveyQuestion[],
  answers: CouponNewUserSurveyQuestionAnswerState[],
): string[] =>
  questions.flatMap((question, index) =>
    (answers[index]?.selectedOptionIds ?? []).map((optionId) => `${getSurveyGtmQuestionId(question.key)}:${optionId}`),
  );

export const countAnsweredSurveyQuestions = (answers: CouponNewUserSurveyQuestionAnswerState[]): number =>
  answers.filter((answer) => answer.selectedOptionIds.length > 0).length;

export const buildSurveyGtmProfileProperties = (
  questions: CouponNewUserSurveyQuestion[],
  answers: CouponNewUserSurveyQuestionAnswerState[],
): Record<string, string | string[] | undefined> =>
  Object.fromEntries(
    questions.map((question, index) => {
      const selectedOptionIds = answers[index]?.selectedOptionIds ?? [];
      const propertyName =
        COUPON_NEW_USER_SURVEY_GTM_PROFILE_PROPERTY_BY_QUESTION_KEY[question.key] ?? `profile_${question.key}`;

      return [
        propertyName,
        question.type === CouponNewUserSurveyQuestionType.Multiple ? selectedOptionIds : selectedOptionIds[0],
      ];
    }),
  );
